<template>
    <div
        v-if="!selectedFile"
        class="mt-4 border-2 border-dashed border-gray-300 py-12 px-4 rounded-lg text-center relative flex flex-col items-center justify-center"
        :class="{ 'bg-gray-50 border-emerald-500': isDragging }"
        @dragover.prevent="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop"
        data-node-id="6:501"
    >
        <!-- アップロードアイコン -->
        <div class="relative shrink-0 w-24 h-24 flex items-center justify-center">
            <div class="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
            <i class="fa-light fa-arrow-up-from-line text-3xl text-gray-700"></i>
            </div>
        </div>

        <!-- テキスト説明 -->
        <div class="space-y-3 py-6">
            <p class="font-bold text-gray-700">
                アップロードする動画ファイルをドラッグ＆ドロップしてください。
            </p>
            <div class="flex justify-center">
            <ul class="text-xs text-left list-outside list-disc pl-4 text-gray-700">
                <li>アップロードできる1ファイルの容量は2GBまでです。</li>
                <li>m4a、mp3、およびm4aファイルに対応しています。</li>
            </ul>
            </div>
        </div>

        <!-- ファイル選択ボタン -->
        <button
            class="btn-primary"
            @click="triggerFileInput"
            data-name="regular_button"
        >
            <span class="font-bold text-base text-white text-center whitespace-nowrap leading-6">
                ファイルを選択する
            </span>
        </button>

        <!-- 隠しファイル入力 -->
        <input
            ref="fileInput"
            type="file"
            class="hidden"
            :accept="acceptedFileTypes"
            @change="handleFileChange"
        />
    </div>
    <!-- ファイル選択後の表示 -->
    <div v-else class="text-center mt-4 space-y-4">
        <div class="px-6 md:px-12 space-y-4">
            <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />

            <div class="w-full">
                <video v-if="videoPreview" controls>
                    <source :src="videoPreview" type="video/mp4">
                    <div class="alert-error items-center mb-4">
                        <i class="fa-solid fa-triangle-exclamation text-alert"></i>
                        お使いのブラウザはビデオタグをサポートしていません。
                        <button type="button" class="alert-close link-styles--gray" @click="clearErrorMessageModal">
                            <i class="fa-light fa-xmark w-6 h-6" title="Close"></i>
                        </button>
                    </div>
                </video>
            </div>
            <p class="text-sm bg-gray-100 p-4 rounded-lg break-words"><span class="text-sub">ファイル名：</span>{{ selectedFile.name }}</p>
            <p class="text-sm text-center mx-auto">
                動画をアップロードしたら、自動的に文字起こしが開始されます。
            </p>
        </div>
        <div class="flex justify-end border-t">
            <button v-if="!isUploading || errorMessage" @click="startUpload" class="btn-primary mt-4 px-6">
                アップロードを開始
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, defineEmits, defineProps } from 'vue'
import { useStore } from 'vuex';
import axios from 'axios';
import ErrorMessage from "../ErrorMessage.vue";

// プロップス
const props = defineProps({
    acceptedFileTypes: {
        type: String,
        default: '.m4a,.mp3,.mp4,video/*,audio/*'
    },
    maxFileSize: {
        type: Number,
        default: 2 * 1024 * 1024 * 1024 // 2GB in bytes
    }
})

// リアクティブデータ
const isDragging = ref(false);
const fileInput = ref(null);
const selectedFile = ref(null);
const videoPreview = ref(null);
const errorMessage = ref("");
const isUploading = ref(false);
const store = useStore();
const accessToken = store.state.accessToken;

// イベント
const emit = defineEmits(['upload-video:video', 'upload-video:start', 'upload-video:failed']);

// メソッド
const handleDragOver = () => {
    isDragging.value = true
}

const handleDragLeave = () => {
    isDragging.value = false
}

const handleDrop = (event) => {
    isDragging.value = false
    
    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
        const file = files[0]
        validateAndEmitFile(file)
    }
}

const triggerFileInput = () => {
    fileInput.value?.click()
}

const handleFileChange = (event) => {
    const files = event.target.files
    if (files && files.length > 0) {
        const file = files[0]
        validateAndEmitFile(file)
    }
    // ファイル入力をリセット（同じファイルを再度選択可能にする）
    event.target.value = ''
}

const validateAndEmitFile = (file) => {
    // ファイルサイズチェック
    if (file.size > props.maxFileSize) {
        emit('upload-video:failed', `ファイルサイズが2GBを超えています。現在のサイズ: ${formatFileSize(file.size)}`);
        return
    }

    // ファイルタイプチェック（簡易版）
    const fileName = file.name.toLowerCase()
    const acceptedExtensions = ['.m4a', '.mp3', '.mp4']
    const hasValidExtension = acceptedExtensions.some(ext => fileName.endsWith(ext))
    
    if (!hasValidExtension && !file.type.startsWith('video/') && !file.type.startsWith('audio/')) {
        emit('error', {
            type: 'type',
            message: '対応していないファイル形式です。m4a、mp3、mp4ファイルをアップロードしてください。'
        })
        return
    }

    // ファイルタイプがMP4で、サイズも2GB以下の場合
    selectedFile.value = file;
    videoPreview.value = URL.createObjectURL(file);

    // ファイルを送信
    emit('fileSelected', file)
}

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

const startUpload = async () => {
    if (!selectedFile.value) {
        errorMessage.value = 'ファイルが選択されていません。';
        return;
    }

    isUploading.value = true;
    let formData = new FormData();
    formData.append('file', selectedFile.value);
    emit("upload-video:start");

    try {
        const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
        const uploadResponse = await axios.post(`${apiEndpoint}/upload-video/`, formData, {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'multipart/form-data'
            },
        });
        // アップロード完了
        emit("upload-video:video", uploadResponse.data);

    } catch (error) {
        console.error("アップロードエラー:", error);
        if (error.response?.status == 403) {
            errorMessage.value = 'チャンネルに対するアクセス権がありません。チャンネル管理者にお問い合わせください。';
        } else if (error.response && error.response.data && error.response.data.error) {
            const errorText = error.response.data.error;
            errorMessage.value = errorText;
        } else {
            errorMessage.value = '送信中にエラーが発生しました。時間を空けて再度お試しください。';
        }
        emit('upload-video:failed', errorMessage.value);
    }
}
</script>
